<template>
  <BasePopup
    v-if="displayPopup"
    :banner-type
    :displayPopup
    @closed="handleClose"
  >
    <AppStoreDownloadBanner
      v-if="bannerType === 'new'"
      variant="small"
      @actioned="handleState(STATE.ACTIONED)"
    />

    <AppDownload
      v-if="bannerType === 'old'"
      :description="i18n(translations.appPopupDescription)"
      :termsDescription="
        i18n(translations.appPopupTermsDescription, {
          logMissingTranslation: false,
        })
      "
      :termsTitle="
        i18n(translations.appPopupTermsTitle, { logMissingTranslation: false })
      "
      :title="i18n(translations.appPopupTitle)"
      tracking-zone="app_download_notification"
      @actioned="handleState(STATE.ACTIONED)"
    />

    <EmailCollector
      v-if="displayEmailCollector"
      source="popup"
      @actioned="handleState(STATE.ACTIONED)"
    />
  </BasePopup>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { POPUP_TYPE } from './LeadCollectionPopup.constants'
import translations from './LeadCollectionPopup.translations'
import AppDownload from './components/AppDownload/AppDownload.vue'
import AppStoreDownloadBanner from './components/AppStoreDownloadBanner/AppStoreDownloadBanner.vue'
import BasePopup from './components/BasePopup/BasePopup.vue'
import EmailCollector from './components/EmailCollector/EmailCollectorAdapter.vue'
import { useLeadCollectionPopup } from './composables/useLeadCollectionPopup'
import { STATE, type StateValue, recordState } from './utils/popupPersistence'

const i18n = useI18n()
const { activePopup, routeName } = useLeadCollectionPopup()
const tracking = useTracking()
const { countryCode } = useMarketplace().market
const runtimeConfig = useRuntimeConfig()
const { ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES } = runtimeConfig.public
const isAppsFlyerWithDidomiEnabled =
  ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES?.includes(countryCode)

const appDownloadPopupType = isAppsFlyerWithDidomiEnabled
  ? POPUP_TYPE.APP_DOWNLOAD_NEW
  : POPUP_TYPE.APP_DOWNLOAD

const displayPopup = ref(false)

const hasActivePopup = computed(() => {
  return !!activePopup.value
})

const displayAppDownload = computed(() => {
  return activePopup.value === appDownloadPopupType
})

const bannerType = computed(() => {
  if (!displayAppDownload.value) return undefined

  if (isAppsFlyerWithDidomiEnabled) return 'new'

  return 'old'
})

const zone = computed(() => {
  if (!displayAppDownload.value) return 'email_collection_notification'

  return bannerType.value === 'new'
    ? 'app_download_new_notification'
    : 'app_download_notification'
})

const displayEmailCollector = computed(() => {
  return activePopup.value === POPUP_TYPE.EMAIL_COLLECTOR
})

function handleState(state: StateValue) {
  if (!activePopup.value) {
    return
  }

  recordState(activePopup.value, state)
  displayPopup.value = false
}

function handleClose() {
  handleState(STATE.CLOSED)

  tracking.trackClick({
    zone: zone.value,
    name: 'closed',
    page_type: routeName,
  })
}

function trackImpression() {
  if (!hasActivePopup.value) return

  tracking.trackPromptNotificationImpression({
    zone: zone.value,
    page_type: routeName,
  })
}

watch(
  () => hasActivePopup.value,
  (hasPopup) => {
    displayPopup.value = hasPopup

    if (hasPopup) {
      trackImpression()
    }
  },
  { immediate: true },
)
</script>
